import React, { useState } from 'react';
import { getTenantToday } from '../../utils/dateUtils';
import axios from 'axios';
import { FileText } from 'lucide-react';
import {
  ModalShell, Body, FieldRow, Field, Label, Input, Select, Footer, Button, ErrorBanner
} from './reModalShared';

import { DateField } from '../shared/DateField';

interface PositionLite {
  id: number;
  propertyName: string;
  propertyType: string;
  location: string;
  occupancyPct: number;  // decimal
}

interface Props {
  position: PositionLite;
  onClose: () => void;
  onSuccess: () => void;
}

const LEASE_TYPES = ['NNN', 'GROSS', 'MODIFIED_GROSS', 'FSG'];

const RealEstateLeaseModal: React.FC<Props> = ({ position, onClose, onSuccess }) => {
  const [tenantName, setTenantName] = useState<string>('');
  const [leaseType, setLeaseType] = useState<string>('NNN');
  const [leaseStart, setLeaseStart] = useState<string>(getTenantToday());
  const [leaseEnd, setLeaseEnd] = useState<string>('');
  const [annualBaseRent, setAnnualBaseRent] = useState<string>('');
  const [rentableSqft, setRentableSqft] = useState<string>('');
  const [escalationPct, setEscalationPct] = useState<string>('3.00');
  const [occupancyPct, setOccupancyPct] = useState<string>(
    position.occupancyPct ? (position.occupancyPct * 100).toFixed(1) : ''
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if (!tenantName.trim()) { setError('Tenant name is required'); return; }
    if (!annualBaseRent || Number(annualBaseRent) <= 0) {
      setError('Annual base rent must be greater than 0'); return;
    }
    if (leaseEnd && leaseStart && leaseEnd < leaseStart) {
      setError('Lease end date cannot be before start date'); return;
    }
    setSubmitting(true);
    try {
      // Backend stores escalation_pct + occupancy_pct as decimals.
      const escalationDecimal = escalationPct ? Number(escalationPct) / 100 : null;
      const occupancyDecimal = occupancyPct ? Number(occupancyPct) / 100 : null;
      await axios.put(`/api/credit/re-equity/${position.id}/lease`, {
        tenantName: tenantName.trim(),
        leaseType,
        leaseStart,
        leaseEnd: leaseEnd || null,
        annualBaseRent: Number(annualBaseRent),
        rentableSqft: rentableSqft ? Number(rentableSqft) : null,
        escalationPct: escalationDecimal,
        occupancyPct: occupancyDecimal
      });
      onSuccess();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to record lease';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title="Record Lease"
      subtitle={`${position.propertyName} • ${position.propertyType} • ${position.location}`}
      icon={<FileText size={18} className="text-amber-400" />}
      onClose={onClose}
    >
      <Body>
        <FieldRow>
          <Field>
            <Label>Tenant / Lessee</Label>
            <Input
              type="text"
              placeholder="e.g. Salesforce, Inc."
              value={tenantName}
              onChange={e => setTenantName(e.target.value)}
            />
          </Field>
          <Field>
            <Label>Lease Type</Label>
            <Select value={leaseType} onChange={e => setLeaseType(e.target.value)}>
              {LEASE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </Select>
          </Field>
        </FieldRow>

        <FieldRow>
          <Field>
            <Label>Lease Start</Label>
            <DateField value={leaseStart ?? ''} onChange={iso => setLeaseStart(iso)} />
          </Field>
          <Field>
            <Label>Lease End</Label>
            <DateField value={leaseEnd ?? ''} onChange={iso => setLeaseEnd(iso)} />
          </Field>
        </FieldRow>

        <FieldRow>
          <Field>
            <Label>Annual Base Rent (USD)</Label>
            <Input
              type="number"
              step="0.01"
              placeholder="e.g. 9800000"
              value={annualBaseRent}
              onChange={e => setAnnualBaseRent(e.target.value)}
            />
          </Field>
          <Field>
            <Label>Rentable Sqft</Label>
            <Input
              type="number"
              step="0.01"
              placeholder="e.g. 436000"
              value={rentableSqft}
              onChange={e => setRentableSqft(e.target.value)}
            />
          </Field>
        </FieldRow>

        <FieldRow>
          <Field>
            <Label>Annual Escalation (%)</Label>
            <Input
              type="number"
              step="0.01"
              placeholder="e.g. 3.00"
              value={escalationPct}
              onChange={e => setEscalationPct(e.target.value)}
            />
          </Field>
          <Field>
            <Label>Building Occupancy (%)</Label>
            <Input
              type="number"
              step="0.1"
              placeholder="e.g. 94.0"
              value={occupancyPct}
              onChange={e => setOccupancyPct(e.target.value)}
            />
          </Field>
        </FieldRow>

        {error && <ErrorBanner>{error}</ErrorBanner>}
      </Body>

      <Footer>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button $primary onClick={submit} disabled={submitting}>
          {submitting ? 'Recording…' : 'Record Lease'}
        </Button>
      </Footer>
    </ModalShell>
  );
};

export default RealEstateLeaseModal;
